import { useState, useEffect } from "react";
import * as myApi from "./Api";

/**
 * Debug Panel - Only visible in non-production environments
 * Shows: Database status, connection info, backend URL, environment details
 */
const DebugPanel = () => {
  const [isVisible, setIsVisible] = useState(false);

  const apiUrl = window.env?.API_URL || process.env.REACT_APP_API_URL || "http://localhost:8080";
  const isDevelopment =
    apiUrl.includes('localhost') ||
    apiUrl.includes('127.0.0.1') ||
    process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  const [dbStatus, setDbStatus] = useState({
    connected: false,
    type: "unknown",
    host: "unknown",
    port: "unknown",
    database: "unknown",
    status: "checking..."
  });
  const [backendInfo, setBackendInfo] = useState({
    url: "unknown",
    status: "checking...",
    responseTime: null
  });
  const [envInfo, setEnvInfo] = useState({
    nodeEnv: "unknown",
    reactAppEnv: "unknown"
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch backend connection info
  const fetchDbInfo = async () => {
    const startTime = Date.now();
    
    try {
      const response = await myApi.get('/db-info');
      const responseTime = Date.now() - startTime;
      
      if (response.data && response.data.status === 'success') {
        const data = response.data.data;
        setDbStatus({
          connected: true,
          type: data.type || 'mysql',
          host: data.host || 'localhost',
          port: data.port || '3306',
          database: data.name || 'unknown',
          status: 'connected'
        });
        setBackendInfo({
          url: apiUrl,
          status: 'online',
          responseTime: `${responseTime}ms`
        });
      }
    } catch (error) {
      setDbStatus({
        connected: false,
        type: 'unknown',
        host: 'unknown',
        port: 'unknown',
        database: 'unknown',
        status: `error: ${error.message}`
      });
      setBackendInfo({
        url: apiUrl,
        status: 'offline',
        responseTime: null
      });
    }
    
    setIsLoading(false);
  };

  // Fetch info on mount
  useEffect(() => {
    if (isDevelopment) {
      fetchDbInfo();
      
      setEnvInfo({
        nodeEnv: process.env.NODE_ENV || 'development',
        reactAppEnv: process.env.REACT_APP_NODE_ENV || 'not set'
      });
    }
  }, [isDevelopment, apiUrl]);

  // Toggle visibility with keyboard shortcut (Ctrl+Shift+D)
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setIsVisible(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Don't render in production
  if (!isDevelopment) {
    return null;
  }

  const getStatusColor = (status) => {
    if (status === 'connected' || status === 'online') return '#22c55e';
    if (status === 'error' || status === 'offline') return '#ef4444';
    return '#f59e0b';
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        style={{
          position: 'fixed',
          bottom: '80px',
          right: '20px',
          zIndex: 9999,
          background: '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          fontSize: '20px',
          cursor: 'pointer',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease'
        }}
        title="Toggle Debug Panel (Ctrl+Shift+D)"
      >
        🐛
      </button>

      {/* Debug Panel */}
      {isVisible && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            background: 'white',
            border: '2px solid #e5e7eb',
            borderRadius: '12px',
            padding: '0',
            width: '420px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            fontFamily: 'monospace'
          }}
        >
          {/* Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '16px 20px',
              borderRadius: '10px 10px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
                🔍 Debug Panel
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
                Non-Production Environment
              </p>
            </div>
            <button
              onClick={() => setIsVisible(false)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: '20px' }}>
            {/* Backend Connection Status */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '14px', 
                fontWeight: 'bold',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                🔗 Backend Connection
              </h4>
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#6b7280', fontSize: '12px' }}>Backend URL:</span>
                  <div style={{ 
                    fontSize: '13px', 
                    fontWeight: 'bold',
                    color: '#1f2937',
                    wordBreak: 'break-all'
                  }}>
                    {backendInfo.url}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Status:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: getStatusColor(backendInfo.status)
                      }}></div>
                      <span style={{ 
                        fontSize: '13px', 
                        fontWeight: 'bold',
                        color: backendInfo.status === 'online' ? '#22c55e' : '#ef4444'
                      }}>
                        {backendInfo.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  {backendInfo.responseTime && (
                    <div>
                      <span style={{ color: '#6b7280', fontSize: '12px' }}>Response:</span>
                      <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1f2937', marginTop: '4px' }}>
                        {backendInfo.responseTime}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Database Status */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '14px', 
                fontWeight: 'bold',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                🗄️ Database Connection
              </h4>
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Database Type:</span>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: 'bold',
                      color: dbStatus.type === 'mysql' ? '#2563eb' : '#7c3aed',
                      marginTop: '4px'
                    }}>
                      {dbStatus.type === 'mysql' ? '🐬 MySQL' : dbStatus.type === 'mongodb' ? '🍃 MongoDB' : dbStatus.type.toUpperCase()}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Status:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: getStatusColor(dbStatus.status)
                      }}></div>
                      <span style={{ 
                        fontSize: '13px', 
                        fontWeight: 'bold',
                        color: dbStatus.connected ? '#22c55e' : '#ef4444'
                      }}>
                        {dbStatus.connected ? 'CONNECTED' : 'DISCONNECTED'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Host:</span>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1f2937', marginTop: '4px' }}>
                      {dbStatus.host}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Port:</span>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1f2937', marginTop: '4px' }}>
                      {dbStatus.port}
                    </div>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Database:</span>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1f2937', marginTop: '4px' }}>
                      {dbStatus.database}
                    </div>
                  </div>
                </div>

                {!dbStatus.connected && dbStatus.status !== 'checking...' && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px',
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    color: '#dc2626',
                    fontSize: '12px'
                  }}>
                    ⚠️ {dbStatus.status}
                  </div>
                )}
              </div>
            </div>

            {/* Environment Info */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '14px', 
                fontWeight: 'bold',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                ⚙️ Environment
              </h4>
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>NODE_ENV:</span>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: 'bold', 
                      color: envInfo.nodeEnv === 'production' ? '#ef4444' : '#22c55e',
                      marginTop: '4px'
                    }}>
                      {envInfo.nodeEnv}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Frontend:</span>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1f2937', marginTop: '4px' }}>
                      React {process.env.REACT_APP_VERSION || '18.2.0'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchDbInfo}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '10px',
                background: isLoading ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {isLoading ? '🔄 Checking...' : '🔄 Refresh Info'}
            </button>

            {/* Keyboard Shortcut Hint */}
            <p style={{
              margin: '12px 0 0 0',
              fontSize: '11px',
              color: '#9ca3af',
              textAlign: 'center'
            }}>
              Shortcut: <kbd style={{ 
                background: '#f3f4f6', 
                padding: '2px 6px', 
                borderRadius: '4px',
                border: '1px solid #d1d5db'
              }}>Ctrl+Shift+D</kbd> to toggle
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default DebugPanel;
